import * as React from "react";
import { cn } from "../lib/utils.ts";
import { Skeleton } from "./skeleton.tsx";

export interface EpisodeRowProps extends React.HTMLAttributes<HTMLAnchorElement> {
  href: string;
  episodeNumber: number;
  title: string;
  durationLabel: string;
  description?: string;
  thumbnailUrl?: string;
  progress?: number;
  active?: boolean;
}

export const EpisodeRow = React.forwardRef<HTMLAnchorElement, EpisodeRowProps>(
  (
    {
      href,
      episodeNumber,
      title,
      durationLabel,
      description,
      thumbnailUrl,
      progress,
      active,
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <a
        ref={ref}
        href={href}
        aria-current={active ? "page" : undefined}
        className={cn(
          "flex items-start gap-3 rounded-lg p-3 transition-colors",
          "hover:bg-[var(--color-secondary)]/60 focus-visible:bg-[var(--color-secondary)]/60",
          "focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]",
          active && "bg-[var(--color-secondary)]",
          className,
        )}
        {...props}
      >
        <div
          className="relative w-32 shrink-0 overflow-hidden rounded-md bg-[var(--color-muted)] md:w-44"
          style={{ aspectRatio: "var(--aspect-still)" }}
        >
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt=""
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          ) : (
            <Skeleton className="h-full w-full" />
          )}
          {typeof progress === "number" && progress > 0 && (
            <div className="absolute inset-x-1 bottom-1 h-1 rounded-full bg-black/60">
              <div
                className="h-full rounded-full bg-[var(--color-accent)]"
                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="truncate text-base font-semibold">
              <span className="text-[var(--color-muted-foreground)]">Ep {episodeNumber}.</span>{" "}
              {title}
            </h3>
            <time
              className="tabular shrink-0 text-xs text-[var(--color-muted-foreground)]"
              data-tabular
            >
              {durationLabel}
            </time>
          </div>
          {description && (
            <p className="mt-1 line-clamp-2 text-sm text-[var(--color-muted-foreground)]">
              {description}
            </p>
          )}
        </div>
      </a>
    );
  },
);
EpisodeRow.displayName = "EpisodeRow";
